import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { recordRepairPaymentRpc } from '@/services/repairs';
import { queryKeys } from '@/lib/queryKeys';
import { parseDatabaseError } from '@/lib/errors';
import { RepairJob } from '@/types/models';
import { Modal } from '@/components/common/Modal';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { DollarSign, Loader2 } from 'lucide-react';

interface RepairPaymentModalProps {
  repair: RepairJob;
  isOpen: boolean;
  onClose: () => void;
}

export const RepairPaymentModal: React.FC<RepairPaymentModalProps> = ({
  repair,
  isOpen,
  onClose,
}) => {
  const queryClient = useQueryClient();
  const balanceDue = Math.max(0, repair.balance_due ?? (repair.total_charge - repair.amount_paid));

  const [amount, setAmount] = useState<number>(balanceDue);
  const [method, setMethod] = useState<string>('cash');
  const [reference, setReference] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      if (amount <= 0) throw new Error('Payment amount must be greater than zero.');
      await recordRepairPaymentRpc({
        repair_id: repair.id,
        amount,
        payment_method: method,
        reference: reference || undefined,
        notes: notes || undefined,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.repairs.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.repairs.detail(repair.id) });
      onClose();
    },
    onError: (err: unknown) => {
      setErrorMsg(parseDatabaseError(err));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    mutation.mutate();
  };

  return (
    <Modal isOpen={isOpen} title={`Record Repair Payment — Ticket #${repair.job_number}`} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4 text-xs">
        {errorMsg && <ErrorAlert message={errorMsg} />}

        <div className="bg-brand-bg p-3 rounded-lg border border-brand-border flex items-center justify-between">
          <div>
            <p className="font-semibold text-brand-muted uppercase">Total Repair Charge</p>
            <p className="text-sm font-bold text-brand-black">Rs. {repair.total_charge.toLocaleString('en-PK')}</p>
          </div>
          <div>
            <p className="font-semibold text-brand-muted uppercase">Amount Paid</p>
            <p className="text-sm font-bold text-brand-green">Rs. {(repair.amount_paid || 0).toLocaleString('en-PK')}</p>
          </div>
          <div>
            <p className="font-semibold text-brand-muted uppercase">Balance Due</p>
            <p className="text-sm font-bold text-brand-red">Rs. {balanceDue.toLocaleString('en-PK')}</p>
          </div>
        </div>

        <div>
          <label className="block font-semibold text-brand-black uppercase mb-1">
            Payment Amount (PKR) <span className="text-brand-red">*</span>
          </label>
          <input
            type="number"
            min={1}
            max={balanceDue}
            value={amount}
            onChange={(e) => setAmount(Number(e.target.value) || 0)}
            className="w-full px-3 py-2 bg-brand-bg border border-brand-border rounded-md text-sm font-bold text-brand-green focus:ring-2 focus:ring-brand-green"
          />
        </div>

        <div>
          <label className="block font-semibold text-brand-black uppercase mb-1">
            Payment Method
          </label>
          <select
            value={method}
            onChange={(e) => setMethod(e.target.value)}
            className="w-full px-3 py-2 bg-brand-bg border border-brand-border rounded-md text-sm text-brand-black focus:ring-2 focus:ring-brand-green"
          >
            <option value="cash">Cash</option>
            <option value="bank_transfer">Bank Transfer</option>
            <option value="mobile_wallet">Mobile Wallet / JazzCash / EasyPaisa</option>
            <option value="card">Card</option>
            <option value="other">Other</option>
          </select>
        </div>

        <div>
          <label className="block font-semibold text-brand-black uppercase mb-1">
            Reference / Transaction ID
          </label>
          <input
            type="text"
            placeholder="e.g. Bank Ref #TX99102"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            className="w-full px-3 py-2 bg-brand-bg border border-brand-border rounded-md text-xs text-brand-black focus:ring-2 focus:ring-brand-green"
          />
        </div>

        <div>
          <label className="block font-semibold text-brand-black uppercase mb-1">
            Notes
          </label>
          <input
            type="text"
            placeholder="Optional payment notes..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full px-3 py-2 bg-brand-bg border border-brand-border rounded-md text-xs text-brand-black focus:ring-2 focus:ring-brand-green"
          />
        </div>

        <div className="flex items-center justify-end space-x-3 pt-3 border-t border-brand-border">
          <button type="button" onClick={onClose} disabled={mutation.isPending} className="px-4 py-2 text-sm font-medium text-brand-muted hover:text-brand-black">
            Cancel
          </button>
          <button
            type="submit"
            disabled={mutation.isPending || amount <= 0}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-brand-green text-white text-sm font-semibold rounded-md hover:bg-brand-green-hover transition-colors disabled:opacity-50 shadow-sm"
          >
            {mutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <DollarSign className="w-4 h-4" />}
            <span>Record Payment</span>
          </button>
        </div>
      </form>
    </Modal>
  );
};
